/**
 * SEO + SSR data fetch + Open Graph / JSON-LD helpers pour la page /pay/[slug].
 *
 * Extrait de `apps/donoor.org/app/pages/pay/[slug].vue` pour alléger la page
 * et garder la logique SEO réutilisable. Le composable :
 *   - récupère les données SSR via useFetch
 *   - calcule les valeurs OG (titre, description, image)
 *   - configure useSeoMeta + useHead (canonical + JSON-LD DonateAction)
 *   - retourne les données SSR exposées au template
 */
import type { Ref } from "vue";
import { truncateForMeta } from "@donoor/utils/string";
import { LOCALE_INTL, type Langue } from "~/utils/langue";

type Translate = (key: string, params?: Record<string, unknown>) => string;

interface CustomField {
  type: "fullname" | "email" | "message";
  required: boolean;
  label: string;
}

type SsrOrganization = NonNullable<
  NonNullable<SSRPaymentData["data"]>["organization"]
>;
type SsrPaymentPage = NonNullable<
  NonNullable<SSRPaymentData["data"]>["paymentPage"]
>;

/** Titre Open Graph dérivé de la page de paiement / organisation. */
function computeOgTitle(
  page: SsrPaymentPage | undefined,
  org: SsrOrganization | undefined,
  t: Translate,
): string {
  if (page?.title) return page.title;
  return org?.name
    ? t("don.paySlugSeo.ogTitreAvecOrg", { nom: org.name })
    : t("don.paySlugSeo.ogTitreParDefaut");
}

/** Description Open Graph dérivée de la page de paiement / organisation. */
function computeOgDescription(
  page: SsrPaymentPage | undefined,
  org: SsrOrganization | undefined,
  t: Translate,
): string {
  // `subtitle` = description du lien de paiement, qui peut faire plusieurs
  // paragraphes : une seule ligne, 160 caractères max (aperçus WhatsApp,
  // résultats Google).
  if (page?.subtitle?.trim()) {
    return truncateForMeta(page.subtitle.replace(/\s+/g, " ").trim(), 160);
  }
  return org?.name
    ? t("don.paySlugSeo.ogDescriptionAvecOrg", { nom: org.name })
    : t("don.paySlugSeo.ogDescriptionParDefaut");
}

/** Titre de la balise <title> de la page. */
function computePageTitle(
  page: SsrPaymentPage | undefined,
  org: SsrOrganization | undefined,
  t: Translate,
): string {
  if (page?.title) return `${page.title} | ${org?.name || "Donoor"}`;
  return org?.name
    ? t("don.paySlugSeo.titrePageAvecOrg", { nom: org.name })
    : t("don.paySlugSeo.titrePageParDefaut");
}

/** Bloc JSON-LD DonateAction (vide si pas d'organisation SSR). */
function buildJsonLdScripts(params: {
  org: SsrOrganization | undefined;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl: string | undefined;
  canonicalUrl: string;
}): Array<{ type: "application/ld+json"; innerHTML: string }> {
  const { org, ogTitle, ogDescription, ogImageUrl, canonicalUrl } = params;
  if (!org) return [];
  return [
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "DonateAction",
        name: ogTitle,
        description: ogDescription,
        url: canonicalUrl,
        ...(ogImageUrl && { image: ogImageUrl }),
        recipient: { "@type": "Organization", name: org.name },
        target: { "@type": "EntryPoint", urlTemplate: canonicalUrl },
      }),
    },
  ];
}

export interface SSRPaymentData {
  success: boolean;
  data?: {
    qrCode?: {
      id: string;
      title?: string;
      description?: string;
      custom_message?: string;
      suggested_amounts?: number[];
    };
    organization?: {
      id: number;
      name: string;
      logo?: string | null;
      stripe_account_id?: string;
    };
    paymentPage?: {
      slug: string;
      banner?: string | null;
      amounts?: number[];
      defaultAmountIndex?: number;
      message?: string | null;
      title?: string | null;
      subtitle?: string | null;
      goalAmount?: number | null;
      goalEndDate?: string | null;
      primaryColor?: string | null;
      backgroundColor?: string | null;
      buttonStyle?: string | null;
      submitText?: string | null;
      youtubeUrl?: string | null;
      subscriptionsEnabled?: boolean;
      customFields?: CustomField[] | null;
      showFeesOption?: boolean;
      showFiscalInfo?: boolean;
      showGoalProgress?: boolean;
    };
    /**
     * Verdict « reçu fiscal annoncé » — au premier niveau car `paymentPage`
     * vaut null pour une asso sans page de paiement (cf. recuFiscalAnnonce).
     */
    showFiscalInfo?: boolean;
    raised?: number;
    goal?: number | null;
    showGoalProgress?: boolean;
  };
}

/**
 * NB SSR Nuxt 4 : après un `await` dans un composable exporté, le contexte
 * Nuxt est perdu. Les appels suivants à `useSeoMeta` / `useHead` (qui
 * dépendent en interne de `useNuxtApp()`) doivent être faits dans
 * `nuxtApp.runWithContext(() => ...)`, sinon erreur SSR
 * `[nuxt] instance unavailable` (HTTP 500 sur la page).
 */
export async function usePaySlugSeo(
  slug: string,
  cacheBuster: string | undefined,
  options?: {
    /**
     * `embed` : rend le formulaire dans un iframe cross-site (route /embed/[slug]).
     * On garde le MÊME fetch de données SSR, mais on n'émet PAS le SEO/Open Graph
     * ni le canonical `/pay/${slug}` : l'iframe n'est pas une page indexable et
     * ne doit pas déclarer de canonical pointant vers /pay (ce serait un doublon
     * SEO). On force juste `robots: noindex, nofollow`. Défaut : `pay`.
     */
    mode?: "pay" | "embed";
  },
): Promise<{
  ssrPaymentData: Ref<SSRPaymentData | null>;
  ssrPaymentPage: NonNullable<SSRPaymentData["data"]>["paymentPage"] | undefined;
}> {
  const nuxtApp = useNuxtApp();
  const config = useRuntimeConfig();
  const isEmbed = options?.mode === "embed";
  const { t, locale } = useI18n();
  const ogLocale = (LOCALE_INTL[locale.value as Langue] ?? "fr-FR").replace("-", "_");

  const { data: ssrPaymentData } = await useFetch<SSRPaymentData>(
    `${config.public.apiUrl}/api/public/payment-link/${slug}`,
    {
      key: cacheBuster
        ? `payment-org-ssr-${slug}-${cacheBuster}`
        : `payment-org-ssr-${slug}`,
      server: true,
      lazy: false,
      // Retry transitoire (timeout/5xx) — éviter le flaky "Lien de paiement
      // non disponible" au premier refresh (Karim 2026-06-21).
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  ).catch(() => ({ data: ref(null) as Ref<SSRPaymentData | null> }));

  const getOgImageUrl = (
    imageId: string | null | undefined,
  ): string | undefined => {
    if (!imageId) return undefined;
    if (imageId.startsWith("http")) return imageId;
    if (imageId.startsWith("/")) return `https://donoor.org${imageId}`;
    const assetsBaseUrl =
      (config.public.apiUrl as string) || "https://api.donoor.org";
    return `${assetsBaseUrl}/api/assets/${imageId}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
  };

  const ssrData = ssrPaymentData.value?.data;
  const ssrOrganization = ssrData?.organization;
  const ssrPaymentPage = ssrData?.paymentPage;

  const ogTitle = computeOgTitle(ssrPaymentPage, ssrOrganization, t);
  const ogDescription = computeOgDescription(ssrPaymentPage, ssrOrganization, t);
  const ogImageUrl =
    getOgImageUrl(ssrPaymentPage?.banner) ||
    getOgImageUrl(ssrOrganization?.logo);
  const pageTitle = computePageTitle(ssrPaymentPage, ssrOrganization, t);

  const canonicalUrl = `https://donoor.org/pay/${slug}`;

  // JSON-LD structuré (DonateAction) — uniquement si on a une org SSR.
  const jsonLdScripts = buildJsonLdScripts({
    org: ssrOrganization,
    ogTitle,
    ogDescription,
    ogImageUrl,
    canonicalUrl,
  });

  // Wrap les composables Nuxt post-await dans runWithContext pour restaurer
  // l'instance Nuxt perdue par le `await useFetch` ci-dessus.
  nuxtApp.runWithContext(() => {
    // Mode embed (iframe cross-site) : pas d'Open Graph / JSON-LD / canonical.
    // On force uniquement noindex,nofollow — l'iframe ne doit jamais être
    // indexée ni revendiquer un canonical vers /pay (doublon SEO).
    if (isEmbed) {
      useSeoMeta({ robots: "noindex, nofollow", title: pageTitle });
      return;
    }

    useSeoMeta({
      title: pageTitle,
      description: ogDescription,
      // Lien de paiement transactionnel par organisation — Open Graph utile pour
      // les partages, mais pas indexable par les moteurs.
      robots: "noindex, nofollow",
      ogType: "website",
      ogTitle: ogTitle,
      ogDescription: ogDescription,
      ...(ogImageUrl && {
        ogImage: ogImageUrl,
        ogImageWidth: 1200,
        ogImageHeight: 630,
      }),
      ogImageAlt: ssrOrganization?.name
        ? `${ssrOrganization.name} - Donoor`
        : "Donoor",
      ogUrl: canonicalUrl,
      ogSiteName: "Donoor",
      ogLocale,
      twitterCard: ogImageUrl ? "summary_large_image" : "summary",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      ...(ogImageUrl && { twitterImage: ogImageUrl }),
      twitterSite: "@donoor_org",
    });

    useHead({
      link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
      script: jsonLdScripts,
    });
  });

  return {
    ssrPaymentData: ssrPaymentData as Ref<SSRPaymentData | null>,
    ssrPaymentPage,
  };
}
